import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import API_BASE from '../config';

// Maps level prop to API table path
const LEVEL_TABLES = {
  account: 'accounts',
  client: 'clients',
  company: 'companies',
  contact: 'contacts'
};

const TONES = [
  { value: 'consultative', label: 'Consultative — diagnostic, earned authority' },
  { value: 'direct', label: 'Direct — confident, no fluff' },
  { value: 'warm', label: 'Warm — human, respectful' },
  { value: 'formal', label: 'Formal — polished, considered' }
];

const INTENTS = [
  { value: 'sell', label: 'Sell — clear about offer, ask for time' },
  { value: 'nurture', label: 'Nurture — build rapport, plant seeds' },
  { value: 'info', label: 'Info — share value, no ask' }
];

const LENGTHS = [
  { value: 'short', label: 'Short — 60–90 words' },
  { value: 'medium', label: 'Medium — 100–140 words' },
  { value: 'long', label: 'Long — 150–200 words' }
];

const MEETING_ASKS = [
  { value: 'specific', label: 'Specific — propose a 20-minute window' },
  { value: 'soft', label: 'Soft — leave the door open' },
  { value: 'none', label: 'None — never ask for a meeting' }
];

function SequenceConfigPanel({ level, id, showResolutionChain = false, contactId = null }) {
  const { getToken } = useAuth();
  const [config, setConfig] = useState(null);
  const [canWrite, setCanWrite] = useState(true);
  const [resolved, setResolved] = useState(null); // { config, source }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState({});
  const [salesStepsInput, setSalesStepsInput] = useState('');

  const table = LEVEL_TABLES[level];

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };

      // Load config at this level
      const r = await fetch(`${API_BASE}/${table}/${id}/sequence-config`, { headers });
      if (r.ok) {
        const data = await r.json();
        setConfig(data.config);
        setDraft(data.config || {});
        setSalesStepsInput(Array.isArray(data.config?.sales_steps) ? data.config.sales_steps.join(',') : '');
        setCanWrite(data.can_write !== false);
      }

      // Load resolved chain if needed (contact panel only)
      if (showResolutionChain && contactId) {
        const rr = await fetch(`${API_BASE}/contacts/${contactId}/resolved-sequence-config`, { headers });
        if (rr.ok) {
          const rd = await rr.json();
          setResolved(rd);
        }
      }
    } catch (e) {
      toast.error('Failed to load sequence config');
    }
    setLoading(false);
  }, [getToken, table, id, showResolutionChain, contactId]);

  useEffect(() => { load(); }, [load]);

  async function save() {
    setSaving(true);
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/${table}/${id}/sequence-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ config: draft })
      });
      if (!r.ok) {
        const err = await r.json();
        toast.error(err.error || 'Failed to save');
        setSaving(false);
        return;
      }
      const data = await r.json();
      setConfig(data.config);
      toast.success('Sequence settings saved');
      // Refresh resolved chain
      if (showResolutionChain && contactId) {
        const rr = await fetch(`${API_BASE}/contacts/${contactId}/resolved-sequence-config`, { headers: { Authorization: `Bearer ${token}` } });
        if (rr.ok) setResolved(await rr.json());
      }
    } catch (e) {
      toast.error('Failed to save sequence config');
    }
    setSaving(false);
  }

  async function clearOverride() {
    if (!window.confirm(`Clear ${level}-level override? Sequences will use the inherited default.`)) return;
    setSaving(true);
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/${table}/${id}/sequence-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ config: null })
      });
      if (r.ok) {
        setConfig(null);
        setDraft({});
        setSalesStepsInput('');
        toast.success('Override cleared');
        if (showResolutionChain && contactId) {
          const rr = await fetch(`${API_BASE}/contacts/${contactId}/resolved-sequence-config`, { headers: { Authorization: `Bearer ${token}` } });
          if (rr.ok) setResolved(await rr.json());
        }
      } else {
        toast.error('Failed to clear');
      }
    } catch (e) {
      toast.error('Failed to clear');
    }
    setSaving(false);
  }

  // Header summary
  function summary() {
    if (loading) return 'Loading...';
    if (!config) {
      if (showResolutionChain && resolved) {
        return `Inherited from ${resolved.source}`;
      }
      return 'Using inherited default';
    }
    const parts = [];
    if (config.intent) parts.push(config.intent);
    if (config.email_count !== undefined) parts.push(`${config.email_count} email${config.email_count !== 1 ? 's' : ''}`);
    if (config.linkedin_count !== undefined) parts.push(`${config.linkedin_count} LinkedIn`);
    return parts.length > 0 ? `Custom — ${parts.join(', ')}` : 'Custom';
  }

  const labelStyle = { display: 'block', fontSize: '0.72rem', fontWeight: '600', color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.3rem' };
  const inputStyle = { width: '100%', padding: '0.5rem 0.7rem', fontSize: '0.85rem', border: '0.5px solid #E8E4E0', borderRadius: '6px', backgroundColor: '#fff', color: '#1A1A1A' };
  const fieldRow = { marginBottom: '0.85rem' };

  return (
    <div style={{ border: '0.5px solid #E8E4E0', borderRadius: '8px', backgroundColor: '#fff', marginBottom: '1rem' }}>
      {/* Header */}
      <div onClick={() => setExpanded(!expanded)} style={{ padding: '0.75rem 1rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: expanded ? '0.5px solid #E8E4E0' : 'none' }}>
        <div>
          <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '600', color: '#1A1A1A' }}>Sequence settings</p>
          <p style={{ margin: '0.15rem 0 0', fontSize: '0.72rem', color: '#888' }}>{summary()}</p>
        </div>
        <span style={{ fontSize: '0.85rem', color: '#888' }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {/* Body */}
      {expanded && (
        <div style={{ padding: '1rem' }}>
          {showResolutionChain && resolved && (
            <div style={{ padding: '0.6rem 0.8rem', backgroundColor: '#FAFAF8', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.78rem', color: '#4A5568' }}>
              Currently resolves to: <strong>{resolved.source}</strong>
              {resolved.source === 'system' && ' (no overrides set anywhere in the chain)'}
            </div>
          )}

          <fieldset disabled={!canWrite} style={{ border: 'none', padding: 0, margin: 0 }}>
          <div style={fieldRow}>
            <label style={labelStyle}>Tone</label>
            <select style={inputStyle} value={draft.tone || ''} onChange={e => setDraft({ ...draft, tone: e.target.value || undefined })}>
              <option value="">— inherit —</option>
              {TONES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div style={fieldRow}>
            <label style={labelStyle}>Intent</label>
            <select style={inputStyle} value={draft.intent || ''} onChange={e => setDraft({ ...draft, intent: e.target.value || undefined })}>
              <option value="">— inherit —</option>
              {INTENTS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div style={fieldRow}>
            <label style={labelStyle}>Length</label>
            <select style={inputStyle} value={draft.length || ''} onChange={e => setDraft({ ...draft, length: e.target.value || undefined })}>
              <option value="">— inherit —</option>
              {LENGTHS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div style={fieldRow}>
            <label style={labelStyle}>Meeting ask</label>
            <select style={inputStyle} value={draft.meeting_ask || ''} onChange={e => setDraft({ ...draft, meeting_ask: e.target.value || undefined })}>
              <option value="">— inherit —</option>
              {MEETING_ASKS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.85rem' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Email count (1–7)</label>
              <input type="number" min="1" max="7" style={inputStyle} value={draft.email_count ?? ''} onChange={e => setDraft({ ...draft, email_count: e.target.value === '' ? undefined : parseInt(e.target.value, 10) })} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>LinkedIn count (0–7)</label>
              <input type="number" min="0" max="7" style={inputStyle} value={draft.linkedin_count ?? ''} onChange={e => setDraft({ ...draft, linkedin_count: e.target.value === '' ? undefined : parseInt(e.target.value, 10) })} />
            </div>
          </div>

          <div style={fieldRow}>
            <label style={labelStyle}>Sales steps (which steps carry the ask)</label>
            <input type="text" style={inputStyle} placeholder="e.g. 2,4 — leave blank for none" value={salesStepsInput} onChange={e => {
              setSalesStepsInput(e.target.value);
              const v = e.target.value.trim();
              if (v === '') { const { sales_steps, ...rest } = draft; setDraft(rest); return; }
              // Parse silently in the background — only commit valid integers
              const parsed = v.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n) && n >= 1 && n <= 7);
              if (parsed.length > 0) setDraft({ ...draft, sales_steps: parsed });
            }} />
          </div>

          <div style={{ ...fieldRow, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" id={`why-now-${level}-${id}`} checked={draft.why_now_required !== false} onChange={e => setDraft({ ...draft, why_now_required: e.target.checked })} />
            <label htmlFor={`why-now-${level}-${id}`} style={{ fontSize: '0.82rem', color: '#1A1A1A', cursor: 'pointer' }}>Require "why now" reference when signals exist</label>
          </div>

          </fieldset>

          {canWrite ? (
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', borderTop: '0.5px solid #E8E4E0', paddingTop: '1rem' }}>
              <button onClick={save} disabled={saving} style={{ fontSize: '0.82rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1, fontWeight: '500' }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              {config && (
                <button onClick={clearOverride} disabled={saving} style={{ fontSize: '0.82rem', color: '#8B1A1A', background: '#fff', border: '0.5px solid #8B1A1A', borderRadius: '6px', padding: '0.5rem 1rem', cursor: saving ? 'not-allowed' : 'pointer' }}>
                  Clear override
                </button>
              )}
            </div>
          ) : (
            <div style={{ marginTop: '1rem', borderTop: '0.5px solid #E8E4E0', paddingTop: '1rem', fontSize: '0.78rem', color: '#888', fontStyle: 'italic' }}>
              Your role cannot edit {level}-level settings. Read-only.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SequenceConfigPanel;
